import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Store } from '@models/store';
import { StoreService } from '@services/store.service';
import { AuthService } from '@services/auth.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { UserService } from '@services/user.service';
import { BorrowFrameRow } from './borrowing-vendors/borrowing-vendors.component';

@Component({
  selector: 'app-borrowing',
  templateUrl: './borrowing.component.html',
  styleUrls: ['./borrowing.component.css', './../base/vendors-base/vendors-base.component.css', './../managed-board.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ]
})
export class BorrowingComponent implements OnInit, OnDestroy {
  storeOptions: any[];
  borrowingStore: Store = null;
  currentStore: Store = new Store();
  storeSubscription: Subscription;
  rentalUsers = [];
  currentRentalUser;
  otherStoreName = '';
  incomingOpen = true;
  outgoingOpen = true;
  rentalOpen = true;
  period: 'day' | 'week' | 'month' = 'month';
  incomingFrames: BorrowFrameRow[] = [];
  outgoingFrames: BorrowFrameRow[] = [];

  constructor(public storeService: StoreService, private userService: UserService, public auth: AuthService, public cdRef: ChangeDetectorRef) {
    this.storeSubscription = this.auth.getStoreObjectMessage().subscribe((store: Store) => {
      this.currentStore = store;
    });
  }

  ngOnInit() {
    this.storeOptions = this.storeService.stores;
    this.currentStore = this.auth.getStoreObject();
    this.setDefaultBorrowingStore();

    this.userService.getRentalUsers(parseInt(this.auth.getStore(), 0)).subscribe(users => {
      this.rentalUsers = users;
      this.currentRentalUser = users[0];
    });
  }

  ngOnDestroy(): void {
    this.storeSubscription.unsubscribe();
  }

  setDefaultBorrowingStore() {
    const store = this.auth.getStore();
    if (this.storeOptions.length === 2) {
      for (let x = 0; x < this.storeOptions.length; x++) {
        if (this.storeOptions[x]._id !== store) {
          this.borrowingStore = this.storeOptions[x];
          this.otherStoreName = this.storeOptions[x].storeName;
          this.cdRef.detectChanges();
          break;
        }
      }
    }
  }

  changeRentalUser({ value }) {
    this.currentRentalUser = this.rentalUsers.find(u => u._id === value);
  }

  get currentAbbreviation(): string {
    return this.storeAbbreviation(this.currentStore);
  }

  get otherAbbreviation(): string {
    return this.storeAbbreviation(this.borrowingStore) || this.otherStoreName;
  }

  get summaryTitle(): string {
    const now = new Date();
    if (this.period === 'day') {
      return now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) + ' Reimbursement Summary';
    }
    if (this.period === 'week') {
      return 'This Week Reimbursement Summary';
    }
    return now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) + ' Reimbursement Summary';
  }

  get incomingDue(): number {
    return this.dueTotal(this.incomingFrames);
  }

  get outgoingDue(): number {
    return this.dueTotal(this.outgoingFrames);
  }

  get referenceLabel(): string {
    const incoming = this.incomingDue;
    const outgoing = this.outgoingDue;
    const difference = Math.abs(outgoing - incoming);
    const amount = difference.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    if (!difference) {
      return amount;
    }
    if (outgoing > incoming) {
      return `${this.otherStoreName} → ${this.currentStore.storeName} ${amount}`;
    }
    return `${this.currentStore.storeName} → ${this.otherStoreName} ${amount}`;
  }

  private dueTotal(rows: BorrowFrameRow[]): number {
    const now = new Date();
    return (rows || []).reduce((sum, row) => {
      if (row.status !== 'due' || row.amountDue == null || !this.inPeriod(row.borrowed, now)) {
        return sum;
      }
      return sum + row.amountDue;
    }, 0);
  }

  private inPeriod(value: string | number, now: Date): boolean {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return false;
    }
    if (this.period === 'day') {
      return date.toDateString() === now.toDateString();
    }
    if (this.period === 'week') {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(start.getDate() + 7);
      return date >= start && date < end;
    }
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  }

  private storeAbbreviation(store: Store): string {
    return store?.abbreviation || (store?.storeName || '').slice(0, 1);
  }
}
